<template>
  <div class="kr-wrapper text-medium">
    <div class="row kr flex-nowrap" @click="expanded = !expanded">
      <collapse-caret
        class="row-item flex-shrink-0 collapse-caret"
        :size="23"
        :expanded="expanded"
      />
      <i class="row-item kr-icon flex-shrink-0"></i>
      <div
        class="row-item kr-title text-medium-1 bold text-truncate d-flex align-items-center"
        :title="activity.keyResult.title"
      >
        <span class="text-truncate" v-no-capture>{{ activity.keyResult.title }}</span>
        <span
          class="expander-icon pointer ml-2"
          :id="'kr-expander-' + activity.keyResult.id"
          @click.stop="goToKrPage"
          ><i></i
        ></span>
        <action-tooltip :target="'kr-expander-' + activity.keyResult.id" text="Open" />
      </div>
    </div>
    <b-collapse v-model="expanded">
      <div class="kr-activity">
        <template v-if="activity.goalAwardEvents.length > 0">
          <div
            class="events-wrapper"
            v-for="(activity, index) in activity.goalAwardEvents"
            :key="'goal-award-activity-' + index"
          >
            <div class="events">
              <award-activity
                :activity="activity"
                :goal="goal"
                :show-team-org-name="false"
                theme-color="secondary"
                @reacted="$emit('reacted')"
              />
            </div>
          </div>
        </template>
        <template v-if="activity.initiativeAwardEvents.length > 0">
          <div
            class="events-wrapper"
            v-for="(activity, index) in activity.initiativeAwardEvents"
            :key="'initiative-award-activity-' + index"
          >
            <div class="events">
              <award-activity
                :activity="activity"
                :goal="goal"
                :show-team-org-name="false"
                theme-color="secondary"
                @reacted="$emit('reacted')"
              />
            </div>
          </div>
        </template>
        <template v-if="activity.checkinEvents.length > 0">
          <div
            class="events-wrapper"
            v-for="(checkinActivity, index) in activity.checkinEvents"
            :key="'checkin-' + index"
          >
            <checkin-activity
              :activity="checkinActivity"
              theme-color="secondary"
              :kr="activity.keyResult"
              :show-team-org-name="false"
              :goal="goal"
              @awarded="$emit('awarded')"
              @reacted="$emit('reacted')"
            />
          </div>
        </template>
        <template v-if="activity.closeReopenEvents.length > 0">
          <div
            class="events-wrapper"
            v-for="(closeReopenActivity, index) in activity.closeReopenEvents"
            :key="'outcome-' + index"
          >
            <outcome-activity
              :activity="closeReopenActivity"
              theme-color="secondary"
              :kr="activity.keyResult"
              :show-team-org-name="false"
              :goal="goal"
              @awarded="$emit('awarded')"
              @reacted="$emit('reacted')"
            />
          </div>
        </template>
        <template v-if="activity.commentEvents.length > 0">
          <div
            class="events-wrapper"
            v-for="(commentActivity, index) in activity.commentEvents"
            :key="'comment-' + index"
          >
            <comment-activity
              :activity="commentActivity"
              :show-team-org-name="false"
              :show-entity-info="false"
              :goal="goal"
              theme-color="secondary"
              @awarded="$emit('awarded')"
              @reacted="$emit('reacted')"
            />
          </div>
        </template>
        <template v-if="activity.initiativeStatusEvents.length > 0">
          <div
            class="events-wrapper"
            v-for="(activity, index) in activity.initiativeStatusEvents"
            :key="'initiative-status-' + index"
          >
            <initiative-status-activity
              :activity="activity"
              :goal="goal"
              theme-color="secondary"
              :show-team-org-name="false"
              @awarded="$emit('awarded')"
              @reacted="$emit('reacted')"
            />
          </div>
        </template>
        <template v-if="activity.newInitiativeEvents.length > 0">
          <div
            class="events-wrapper"
            v-for="(activity, index) in activity.newInitiativeEvents"
            :key="'new-initiative-' + index"
          >
            <initiative-activity
              :activity="activity"
              theme-color="secondary"
              :show-team-org-name="false"
              :goal="goal"
              @awarded="$emit('awarded')"
              @reacted="$emit('reacted')"
            />
          </div>
        </template>
        <template v-if="activity.closedInitiativeEvents.length > 0">
          <div class="events-wrapper">
            <div class="activity-heading bold text-medium-1">
              These initiatives were closed
            </div>
            <div class="events">
              <template v-for="(activity, index) in activity.closedInitiativeEvents">
                <initiative-activity-row
                  :is-closed="true"
                  :activity="activity"
                  :key="'closed-initiative-' + index"
                />
              </template>
            </div>
          </div>
        </template>
        <template v-if="activity.initiativeCommentEvents.length > 0">
          <div
            class="events-wrapper"
            v-for="(activity, index) in activity.initiativeCommentEvents"
            :key="'initiative-comment-' + index"
          >
            <div class="events">
              <comment-activity
                :activity="activity"
                :show-team-org-name="false"
                :show-entity-info="true"
                :goal="goal"
                theme-color="secondary"
                @awarded="$emit('awarded')"
                @reacted="$emit('reacted')"
              />
            </div>
          </div>
        </template>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import CheckinActivity from '@/components/activity/CheckinActivity'
import InitiativeActivityRow from '@/components/activity/InitiativeActivityRow'
import InitiativeActivity from '@/components/activity/InitiativeActivity'
import InitiativeStatusActivity from '@/components/activity/InitiativeStatusActivity'
import CommentActivity from '@/components/activity/CommentActivity'
import ActionTooltip from '@/components/ActionTooltip'
import OutcomeActivity from '@/components/activity/OutcomeActivity'
import AwardActivity from '@/components/activity/AwardActivity'
import CollapseCaret from '@/components/CollapseCaret'
import handleClickNavigation from '@/utils/handleClickNavigation'
import { getKrPageRouteConfig } from '@/utils/navigationConfigs'
import { BCollapse } from 'bootstrap-vue'

/**
 * This components shows activities on a KR. It shows below activities
 * - New Checkin
 * - New Initiatives
 * - Closed Initiatives
 * - Comments on Initiatives
 * - Awards on KRs/Initiatives
 */
export default {
  name: 'KrActivity',
  components: {
    CheckinActivity,
    InitiativeActivityRow,
    CommentActivity,
    BCollapse,
    ActionTooltip,
    InitiativeActivity,
    InitiativeStatusActivity,
    AwardActivity,
    OutcomeActivity,
    CollapseCaret
  },
  props: ['activity', 'goal'],
  data() {
    return {
      expanded: true
    }
  },
  computed: {
    ownerId() {
      let primaryOwnership = this.activity.keyResult.ownerships.find(
        ownership => ownership.isPrimary
      )
      return primaryOwnership ? primaryOwnership.userId : undefined
    }
  },
  methods: {
    goToKrPage(event) {
      let keyResult = this.activity.keyResult
      const location = getKrPageRouteConfig({
        orgId: keyResult.organizationId,
        krId: keyResult.id
      })
      handleClickNavigation.call(this, event, location)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';

$item-spacer: 8px;
.row-item {
  margin-left: $item-spacer;
}

.kr-wrapper {
  padding-left: 19px;
}

.kr-icon {
  margin-top: 6px;
  transform: scale(0.8);
  height: 15px;
  width: 13px;
  background-size: contain;
}

.events-wrapper {
  margin-bottom: 20px;
  border-radius: 10px;
  background-color: $brandSecondary;
  box-shadow: 0px 1px 1px #dee1e6;
  padding: 12px 20px 12px 20px;
  margin-left: 22px;
  position: relative;
}

.kr {
  margin-bottom: 12px;
  cursor: pointer;

  &:hover .collapse-caret /deep/ .caret {
    opacity: 1;
  }
}

.kr-activity {
  padding-bottom: 1px;
}

@include media-breakpoint-down(md) {
  .kr-wrapper {
    padding-left: 0;
  }
}
</style>
